import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import {
  parseBidSnapshotApplyJob,
  type BidSnapshotApplyJob,
} from '@/api/bidSnapshotTypes';

const POLL_MS = 2000;
const TERMINAL_JOB_STATUSES = new Set([
  'completed',
  'completed_with_errors',
  'failed',
]);

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function pollApplyJobUntilDone(
  profileId: string,
  jobId: string,
): Promise<BidSnapshotApplyJob> {
  for (;;) {
    const res = await adsApi.getBidSnapshotApplyJob(profileId, jobId);
    const job = parseBidSnapshotApplyJob(res.data);
    if (!job) {
      throw new Error('Invalid apply job response');
    }
    if (TERMINAL_JOB_STATUSES.has(job.status)) {
      return job;
    }
    await sleep(POLL_MS);
  }
}

export function useBidSnapshotRestore(profileId: string) {
  const queryClient = useQueryClient();
  const [restoringJob, setRestoringJob] = useState<BidSnapshotApplyJob | null>(null);
  const abortRef = useRef(false);

  useEffect(() => {
    abortRef.current = false;
    return () => {
      abortRef.current = true;
    };
  }, [profileId]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['bid-snapshots', profileId] });
    void queryClient.invalidateQueries({ queryKey: ['bid-snapshot-apply-job', profileId] });
    void queryClient.invalidateQueries({ queryKey: ['bid-snapshot-apply-jobs', profileId] });
    void queryClient.invalidateQueries({ queryKey: ['sp-keywords'] });
    void queryClient.invalidateQueries({ queryKey: ['sb-keywords'] });
    void queryClient.invalidateQueries({ queryKey: ['sp-targets'] });
  }, [profileId, queryClient]);

  const mutation = useMutation({
    mutationFn: async (snapshotId: string) => {
      const createRes = await adsApi.applyBidSnapshot(profileId, snapshotId);
      const created = parseBidSnapshotApplyJob(createRes.data);
      if (!created) {
        throw new Error('Failed to start restore');
      }
      setRestoringJob(created);
      const final = await pollApplyJobUntilDone(profileId, created.id);
      if (abortRef.current) {
        return final;
      }
      setRestoringJob(final);
      return final;
    },
    onSettled: () => {
      invalidate();
    },
  });

  const isRestoring =
    mutation.isPending ||
    (restoringJob != null && !TERMINAL_JOB_STATUSES.has(restoringJob.status));

  return {
    restore: mutation.mutateAsync,
    isRestoring,
    restoringJob,
    reset: () => {
      setRestoringJob(null);
      mutation.reset();
    },
  };
}
